export const AUTH_SESSION_STORAGE_KEY = 'bc_portal_auth_session';
export const AUTH_EXPIRED_EVENT = 'bw:auth-expired';
export const AUTH_REFRESHED_EVENT = 'bw:auth-refreshed';
export const AUTH_REFRESH_LOCK_NAME = 'bw-portal-auth-refresh';
export const AUTH_REFRESH_LEAD_MS = 2 * 60 * 1000;
export const AUTH_REFRESH_RETRY_MS = 30 * 1000;

const CONFIRMED_SESSION_END_CODES = new Set([
  'REFRESH_TOKEN_EXPIRED',
  'REFRESH_TOKEN_INVALID',
  'REFRESH_TOKEN_MISSING',
  'SESSION_REVOKED',
]);

const ACCESS_AUTH_ERROR_CODES = new Set([
  'ACCESS_TOKEN_EXPIRED',
  'ACCESS_TOKEN_INVALID',
  'ACCESS_TOKEN_MISSING',
  'SESSION_REVOKED',
]);

const normalizeTimestamp = (value) => {
  const timestamp = Number(value);
  return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : 0;
};

export const normalizeAuthUrl = (url = '') => `${url || ''}`.replace(/^\//, '').split('?')[0];

export const isLoginRequest = (url) => normalizeAuthUrl(url) === 'auth/token';
export const isLogoutRequest = (url) => normalizeAuthUrl(url) === 'auth/logout';
export const isRefreshRequest = (url) => normalizeAuthUrl(url) === 'auth/refresh-token';

export const readBrowserSession = () => {
  if (typeof window === 'undefined') return {};
  try {
    const value = JSON.parse(window.localStorage.getItem(AUTH_SESSION_STORAGE_KEY) || '{}');
    return {
      tokenExpireAt: normalizeTimestamp(value.tokenExpireAt),
      refreshedAt: normalizeTimestamp(value.refreshedAt),
    };
  } catch (_error) {
    return {};
  }
};

export const storeBrowserSession = (tokenExpireAt, refreshedAt = Date.now()) => {
  const session = {
    tokenExpireAt: normalizeTimestamp(tokenExpireAt),
    refreshedAt: normalizeTimestamp(refreshedAt),
  };
  if (typeof window !== 'undefined' && session.tokenExpireAt) {
    window.localStorage.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(session));
    window.dispatchEvent(new CustomEvent(AUTH_REFRESHED_EVENT, { detail: session }));
  }
  return session;
};

export const clearBrowserSession = () => {
  if (typeof window !== 'undefined') window.localStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
};

export const getAuthErrorCode = (errorOrData = {}) =>
  errorOrData?.errors?.code || errorOrData?.response?.data?.errors?.code || '';

export const isConfirmedSessionEnd = (errorOrData) =>
  CONFIRMED_SESSION_END_CODES.has(getAuthErrorCode(errorOrData));

export const isAccessAuthError = (errorOrData) => ACCESS_AUTH_ERROR_CODES.has(getAuthErrorCode(errorOrData));

export const isRefreshRace = (errorOrData) => getAuthErrorCode(errorOrData) === 'REFRESH_TOKEN_REUSED';

export const shouldAttemptRefresh = ({ status, url, retried, skipAuthRefresh }) =>
  status === 401 && !retried && !skipAuthRefresh && !isLoginRequest(url) && !isRefreshRequest(url);

export const getProactiveRefreshDelay = (tokenExpireAt, now = Date.now()) => {
  const remainingMs = Math.max(0, normalizeTimestamp(tokenExpireAt) - normalizeTimestamp(now));
  if (remainingMs > AUTH_REFRESH_LEAD_MS) return remainingMs - AUTH_REFRESH_LEAD_MS;

  // When a deployment intentionally uses a short access-token lifetime, an
  // immediate refresh after every successful refresh would create a tight
  // loop. Refresh halfway through the remaining lifetime instead.
  return Math.floor(remainingMs / 2);
};
